import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { HC_CONTAINER_PROPS } from "../../ExDashboard/components/chartTheme";
import { MD } from "./mdTheme";
import { MdWidgetCard } from "./MdCardShell";
import { MdMoneyValue } from "./MdMoneyValue";

const RING_SIZE = 120;

/**
 * Matches Bayanat DD targetRing(): doughnut cutout 78%,
 * blue when under target, green when reached/over, track #eef0f3.
 */
const TargetRingCard = ({
  eyebrow,
  targetAmount,
  saleAmount,
  loading,
  loginUserCurrency,
  translate,
}) => {
  const sale = Number(saleAmount) || 0;
  const target = Number(targetAmount) || 0;
  const remaining = Math.max(target - sale, 0);
  const pct = target > 0 ? (sale / target) * 100 : 0;
  const reached = pct >= 100;
  const over = sale > target && target > 0;
  const ringColor = reached ? MD.good : MD.chart[0];
  const filled = target > 0 ? Math.min(sale, target) : 0;
  const empty = target > 0 ? Math.max(target - sale, 0) : 1;

  const ringOptions = useMemo(() => {
    if (!target && !sale) return null;

    // DD: [min(reach,target), max(target-reach,0)] — full green ring when over target
    const data =
      target > 0
        ? empty > 0
          ? [
              { name: "Reached", y: Math.max(filled, 0.0001), color: ringColor },
              { name: "Remaining", y: empty, color: "#eef0f3" },
            ]
          : [{ name: "Reached", y: Math.max(filled, 1), color: ringColor }]
        : [{ name: "Empty", y: 1, color: "#eef0f3" }];

    return {
      chart: {
        type: "pie",
        height: RING_SIZE,
        width: RING_SIZE,
        backgroundColor: "transparent",
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
        animation: false,
        style: { fontFamily: MD.fontUi },
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        pie: {
          innerSize: "78%",
          size: "100%",
          borderWidth: 0,
          dataLabels: { enabled: false },
          showInLegend: false,
          allowPointSelect: false,
          enableMouseTracking: false,
          animation: false,
          slicedOffset: 0,
          states: {
            hover: { enabled: false },
            inactive: { opacity: 1 },
          },
        },
      },
      series: [{ type: "pie", name: "Target", data }],
    };
  }, [filled, empty, ringColor, target, sale, reached]);

  return (
    <MdWidgetCard
      loading={loading}
      eyebrow={eyebrow}
      crumb="(w/o VAT)"
      bigTitle
      title={
        <Tooltip
          title={
            <Typography sx={{ fontSize: 14 }}>
              {target.toLocaleString()}
            </Typography>
          }
        >
          <span>
            <MdMoneyValue
              amount={target}
              currency={loginUserCurrency}
              fontSize={22}
              showTooltip={false}
            />
          </span>
        </Tooltip>
      }
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          mt: 0.5,
          flex: 1,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: RING_SIZE,
            height: RING_SIZE,
            flex: "none",
            overflow: "hidden",
          }}
        >
          {ringOptions ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={ringOptions}
              containerProps={{
                ...HC_CONTAINER_PROPS,
                style: {
                  height: RING_SIZE,
                  width: RING_SIZE,
                  overflow: "hidden",
                },
              }}
              immutable
            />
          ) : (
            <Box
              sx={{
                bgcolor: MD.line2,
                borderRadius: "50%",
                height: RING_SIZE,
                width: RING_SIZE,
              }}
            />
          )}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              pointerEvents: "none",
              textAlign: "center",
            }}
          >
            <Box>
              <Typography
                component="b"
                sx={{
                  color: reached ? MD.good : MD.fg,
                  display: "block",
                  fontFamily: MD.fontHead,
                  fontSize: 17,
                  fontWeight: 600,
                  lineHeight: 1.1,
                }}
              >
                {Number.isFinite(pct) ? `${Math.min(pct, 999).toFixed(0)}%` : "0%"}
              </Typography>
              <Typography
                component="span"
                sx={{ display: "block", fontSize: 10, color: MD.muted, fontWeight: 500 }}
              >
                {translate("reached") || "reached"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          component="dl"
          sx={{
            m: 0,
            display: "grid",
            gridTemplateColumns: "auto auto",
            gap: "4px 12px",
            fontSize: 12,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography component="dt" sx={{ color: MD.muted, m: 0 }}>
            {translate("sale") || "Sale"}
          </Typography>
          <Typography
            component="dd"
            sx={{
              m: 0,
              textAlign: "right",
              fontFamily: MD.fontHead,
              fontWeight: 600,
              color: MD.fg,
            }}
          >
            <MdMoneyValue
              amount={sale}
              currency={loginUserCurrency}
              fontSize={12}
              fontWeight={600}
              showTooltip={false}
              sx={{ justifyContent: "flex-end" }}
            />
          </Typography>
          <Typography component="dt" sx={{ color: MD.muted, m: 0 }}>
            {translate("target") || "Target"}
          </Typography>
          <Typography
            component="dd"
            sx={{
              m: 0,
              textAlign: "right",
              fontFamily: MD.fontHead,
              fontWeight: 600,
              color: MD.fg,
            }}
          >
            <MdMoneyValue
              amount={target}
              currency={loginUserCurrency}
              fontSize={12}
              fontWeight={600}
              showTooltip={false}
              sx={{ justifyContent: "flex-end" }}
            />
          </Typography>
          <Typography component="dt" sx={{ color: MD.muted, m: 0 }}>
            {over
              ? translate("over") || "Over"
              : translate("remaining") || "Remaining"}
          </Typography>
          <Typography
            component="dd"
            sx={{
              m: 0,
              textAlign: "right",
              fontFamily: MD.fontHead,
              fontWeight: 600,
              color: over ? MD.good : MD.fg,
            }}
          >
            <MdMoneyValue
              amount={over ? sale - target : remaining}
              currency={loginUserCurrency}
              fontSize={12}
              fontWeight={600}
              color={over ? MD.good : "inherit"}
              showTooltip={false}
              sx={{ justifyContent: "flex-end" }}
            />
          </Typography>
        </Box>
      </Stack>
    </MdWidgetCard>
  );
};

export default TargetRingCard;
